"use client";

import { useEffect, useRef } from "react";
import Image from "next/image";
import { gsap } from "gsap";
import { EASE } from "@/lib/animations";
import type { Collection } from "@/types";

const FALLBACKS = [
  "linear-gradient(160deg,#4a3320,#1a1410)",
  "linear-gradient(160deg,#3a2c1c,#181310)",
  "linear-gradient(160deg,#4a3a22,#201810)",
  "linear-gradient(160deg,#382e22,#151210)",
];

interface HeroCardProps {
  collection: Collection;
  index: number;
  offset: number;
  cardWidth: number;
  gap: number;
  onSelect: (index: number) => void;
}

export function HeroCard({
  collection,
  index,
  offset,
  cardWidth,
  gap,
  onSelect,
}: HeroCardProps) {
  const cardRef = useRef<HTMLDivElement>(null);
  const prevOffset = useRef(offset);
  const isCenter = offset === 0;
  const distance = Math.abs(offset);

  // Target visual values based on offset
  const targetScale = isCenter ? 1 : distance === 1 ? 0.76 : 0.56;
  const targetOpacity = isCenter ? 1 : distance === 1 ? 0.6 : 0.32;
  const targetBlur = isCenter ? 0 : distance === 1 ? 1.5 : 3;
  const targetX = offset * gap;
  const targetZ = 10 - distance;

  useEffect(() => {
    if (!cardRef.current) return;

    const reduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    const el = cardRef.current;

    // First render: set without animation
    if (prevOffset.current === offset) {
      gsap.set(el, {
        x: targetX,
        scale: targetScale,
        opacity: targetOpacity,
        filter: `blur(${targetBlur}px)`,
        zIndex: targetZ,
      });
      return;
    }

    prevOffset.current = offset;

    if (reduced) {
      gsap.set(el, {
        x: targetX,
        scale: targetScale,
        opacity: targetOpacity,
        filter: `blur(${targetBlur}px)`,
        zIndex: targetZ,
      });
    } else {
      gsap.to(el, {
        x: targetX,
        scale: targetScale,
        opacity: targetOpacity,
        filter: `blur(${targetBlur}px)`,
        zIndex: targetZ,
        duration: 0.65,
        ease: EASE.out,
        overwrite: "auto",
      });
    }
  }, [offset, targetX, targetScale, targetOpacity, targetBlur, targetZ]);

  return (
    <div
      ref={cardRef}
      role="button"
      tabIndex={0}
      aria-label={`${collection.category}, collection ${collection.collectionNumber}${isCenter ? ", current" : ""}`}
      onClick={() => onSelect(index)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect(index);
        }
      }}
      className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 cursor-pointer overflow-hidden rounded-2xl border border-white/10 will-change-[transform,opacity,filter]"
      style={{
        width: cardWidth,
        height: cardWidth * 1.62,
        background: FALLBACKS[index % FALLBACKS.length],
        boxShadow: isCenter
          ? "0 22px 54px rgba(217,154,78,0.22), 0 8px 24px rgba(0,0,0,0.5)"
          : "0 16px 32px rgba(0,0,0,0.35)",
      }}
    >
      <Image
        src={collection.cardImage}
        alt={collection.category}
        fill
        sizes="220px"
        className="object-cover"
        draggable={false}
      />
      <div
        className="absolute inset-0 flex flex-col justify-end p-4"
        style={{
          background:
            "linear-gradient(0deg,rgba(0,0,0,0.88) 0%,rgba(0,0,0,0.12) 55%,transparent 100%)",
        }}
      >
        <p className="text-sm font-bold text-white">{collection.category}</p>
        <p className="mt-1 text-[10px] uppercase tracking-[2px] text-white/65">
          Collection {collection.collectionNumber}
        </p>
      </div>
    </div>
  );
}